import React from 'react';
import {string, bool, func} from 'prop-types';
import {Menu, Message, Button, Loader} from 'semantic-ui-react';
import BasicModal from 'components/common/BasicModal';
import {isNoU, isBlank, removeExtension} from 'utils/DBUtils';
import {makeFormValidator} from 'utils/forms';
import ImageUploadDeviceTab from './ImageUploadDeviceTab.jsx';
import ImageUploadLinkTab from './ImageUploadLinkTab.jsx';
import ImageCropStep from './ImageCropStep.jsx';
import ImageUploadInfoStep, {infoFormConfig} from './ImageUploadInfoStep.jsx';

const validateImageInfo = makeFormValidator(infoFormConfig);

const DeviceTab = 'device';
const LinkTab = 'link';

const GIF_CONTENT_TYPE = 'image/gif';
// Best-effort only -- a URL's extension isn't authoritative (the server
// Tika-sniffs the real content type), but it's enough to avoid offering an
// interactive crop that would flatten an animated GIF's frames.
const GIF_URL_PATTERN = /\.gif(?:[?#]|$)/i;

// Crop-step modes, shared by both tabs. Cropping is a mandatory step for
// any raster source (both tabs) -- Confirm from a plain preview is only
// available for GIFs, which are never cropped (re-encoding would flatten
// the animation).
//
// Ordering matches what production Beaker actually shows: Crop first, then
// the "Image Information" step (name/filename/source/alt text), then
// Confirm. An earlier version of this component had that backwards --
// it followed only the Redux-tracked half of the legacy trail
// (pickedOriginalAction -> pendingImages ->
// UploadOriginalEditImageModalContainer's Info modal, then later a
// separate app-triggered cropOriginal/cropFiles call in createImageEpic)
// and concluded Info came first. That trail is real, but it isn't the
// whole picture: FileStackClient.pick() (observables/lettables/pickFile.js)
// is Filestack's own hosted picker widget, and Filestack's picker includes
// its own in-widget crop/adjust screen *before* its promise resolves and
// before PICKED_ORIGINAL (and our Info modal) ever fires -- invisible to
// Redux, so the epic trail alone misses it. That in-widget step is what
// users actually see first, and it's what this component now mirrors:
// Device sources jump straight from file-select into Cropping
// (handleDeviceFileSelected); Link sources stay in Preview only long enough
// for the user to enter a URL and click Continue (handleContinueFromPreview),
// which fetches the URL and enters Cropping. Info opens only after a crop
// is applied and reviewed (ReviewCrop -> Info), and is the terminal step --
// its "Confirm" sends the upload. GIFs skip Cropping/ReviewCrop entirely
// (nothing to crop) and go straight from Preview/file-select into Info.
const CropMode = {
  Preview: 'preview',       // full image/URL, nothing picked yet -- Link tab only
  Info: 'info',             // "Image Information" step -- name/source/alt text, always shown
  Cropping: 'cropping',     // interactive Cropper open -- mandatory for non-GIF sources
  ReviewCrop: 'reviewCrop'  // showing the result of a completed crop -- Confirm sends it (preCropped: true)
};

// Best-effort filename for a pasted URL -- used only as the `name` passed to
// onConfirm; not required to be perfect since it's just a display/record name.
function deriveNameFromUrl(url) {
  try {
    const withoutQuery = url.split(/[?#]/)[0];
    const segments = withoutQuery.split('/').filter(Boolean);
    return segments.length > 0 ? segments[segments.length - 1] : url;
  } catch (err) {
    return url;
  }
}

/*
    Native replacement for the Filestack picker/crop modal. Presentational
    only -- dispatches nothing and calls no API of its own (the one
    exception is `fetchRemoteImage`, passed in as a prop by
    ImageUploadModalContainer, which this component calls directly -- see
    below). See the "Contract with §4" section of
    working/beaker-image-upload-component-plan.md for the props this must
    honor exactly, and §7 for the fetchRemoteImage addition.

    Renders inside components/common/BasicModal, following the pattern at
    components/images/ImageEditModal.jsx:11-45 for props/actions/in-flight
    handling.

    Both tabs share the same optional crop step (CropMode). The Link tab's
    default (uncropped) path never touches a canvas, so it works for any
    URL and is handled entirely server-side (the server center-crops).
    Entering the interactive cropper for a Link source, however, needs
    same-origin pixel data, so this component fetches the URL through
    admin-api's GET /image/fetch (§6's RemoteImageFetcher) via the injected
    `fetchRemoteImage` prop, wraps the returned Blob in a same-origin File,
    and hands that File to ImageCropStep exactly like a device upload --
    see the approach doc's "The crop canvas only ever sees same-origin
    bytes" paragraph. There is no client-side crossOrigin image load or
    CORS probe anywhere in this flow any more.
*/
export default class ImageUploadModal extends React.Component {
  constructor(props) {
    super(props);
    this.cropStepRef = React.createRef();
    // Bumped every time an in-flight fetchRemoteImage call is invalidated
    // (URL edited, tab switched, modal reopened) so a late response can be
    // told apart from the one that's still wanted -- see handleEnterCrop.
    this.remoteFetchRequestId = 0;
    this.isUnmounted = false;
    this.state = this.getInitialState();
  }

  getInitialState = () => ({
    activeTab: DeviceTab,
    selectedFile: null,
    devicePreviewUrl: null,
    mediaUrl: '',
    cropMode: CropMode.Preview,
    // Set once a crop is applied -- holds the already-processed
    // {file, name, contentType} plus a preview object URL, so Confirm just
    // hands off what was already computed instead of re-cropping.
    croppedResult: null,
    // The Link tab's fetched-through-the-server File, and bookkeeping for
    // that fetch -- see handleEnterCrop. remoteFileUrl is the trimmed URL
    // it was fetched for, so Back -> Crop and Recrop can reuse it instead
    // of refetching, and so editing the URL invalidates it.
    remoteFile: null,
    remoteFileUrl: null,
    remoteFetchStatus: 'idle', // 'idle' | 'pending' | 'failed'
    remoteFetchError: null,
    remoteFetchRetryable: false,
    // Populated when a source is picked (handleDeviceFileSelected /
    // handleContinueFromPreview), edited via ImageUploadInfoStep once Info
    // opens, and read by handleConfirmClick.
    imageInfo: {name: '', filename: '', altAttribute: '', source: '', link: '', sponsoredType: this.props.sponsoredType},
    imageInfoErrors: {},
    error: null,
    isSubmitting: false
  });

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      this.remoteFetchRequestId += 1;
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(this.getInitialState());
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.revokeObjectUrls();
  }

  revokeObjectUrls = () => {
    if (this.state.devicePreviewUrl) {
      URL.revokeObjectURL(this.state.devicePreviewUrl);
    }
    if (this.state.croppedResult) {
      URL.revokeObjectURL(this.state.croppedResult.previewUrl);
    }
  };

  getTrimmedMediaUrl = () => (this.state.mediaUrl || '').trim();

  hasSource = () => (this.state.activeTab === DeviceTab
    ? !isNoU(this.state.selectedFile)
    : !isBlank(this.getTrimmedMediaUrl()));

  isGifSource = () => (this.state.activeTab === DeviceTab
    ? (!isNoU(this.state.selectedFile) && this.state.selectedFile.type === GIF_CONTENT_TYPE)
    : GIF_URL_PATTERN.test(this.getTrimmedMediaUrl()));

  // Switching tabs only changes which tab is displayed -- it must not
  // discard whatever the user picked on either tab (a selected device file,
  // a pasted URL, a completed crop, Info already filled in, ...), so
  // switching back resumes exactly where it was left. The one thing that
  // does need to be invalidated is an in-flight Link-tab fetch: a late
  // response landing while a different tab is active would otherwise
  // clobber state the two tabs share (cropMode, imageInfo, ...) -- see
  // hasSource()'s tab-scoping for why that sharing is otherwise safe.
  handleTabChange = (tab) => {
    if (tab === this.state.activeTab) {
      return;
    }
    if (this.state.remoteFetchStatus === 'pending') {
      this.remoteFetchRequestId += 1;
      this.setState({activeTab: tab, error: null, remoteFetchStatus: 'idle'});
      return;
    }
    this.setState({activeTab: tab, error: null});
  };

  handleDeviceFileSelected = (file) => {
    // Go straight into Cropping for a raster file (see the CropMode comment
    // above); GIFs have nothing to crop, so they go straight into Info,
    // which is now the terminal step -- see handleInfoContinue.
    const isGif = file.type === GIF_CONTENT_TYPE;
    this.setState({
      selectedFile: file,
      devicePreviewUrl: URL.createObjectURL(file),
      cropMode: isGif ? CropMode.Info : CropMode.Cropping,
      imageInfo: {
        name: removeExtension(file.name),
        filename: file.name,
        altAttribute: '',
        source: '',
        link: '',
        sponsoredType: this.props.sponsoredType
      },
      imageInfoErrors: {},
      croppedResult: null,
      error: null
    });
  };

  handleDeviceError = (message) => {
    this.setState({error: message});
  };

  handleReplaceFile = () => {
    this.revokeObjectUrls();
    this.setState({
      selectedFile: null,
      devicePreviewUrl: null,
      cropMode: CropMode.Preview,
      croppedResult: null,
      error: null
    });
  };

  // Editing the URL invalidates any crop (and any fetched-for-cropping File)
  // that was based on the previous one.
  handleMediaUrlChange = (value) => {
    if (this.state.croppedResult) {
      URL.revokeObjectURL(this.state.croppedResult.previewUrl);
    }
    this.remoteFetchRequestId += 1;
    this.setState({
      mediaUrl: value,
      error: null,
      cropMode: CropMode.Preview,
      croppedResult: null,
      remoteFile: null,
      remoteFileUrl: null,
      remoteFetchStatus: 'idle',
      remoteFetchError: null,
      remoteFetchRetryable: false
    });
  };

  handleCancel = () => {
    this.props.onCancel();
  };

  // Enters the interactive cropper -- an optional tool, not a required step.
  // Device tab: the selected File is already in hand, so this just flips
  // the mode. Link tab: the cropper needs same-origin bytes, so this fetches
  // the URL through admin-api's GET /image/fetch (§6) first, unless a File
  // already fetched for this exact URL is cached in state.remoteFile (Back
  // -> Crop, or Recrop, must not refetch).
  handleEnterCrop = () => {
    if (this.state.activeTab === DeviceTab) {
      this.setState({cropMode: CropMode.Cropping, error: null});
      return;
    }

    const trimmedUrl = this.getTrimmedMediaUrl();
    if (!isNoU(this.state.remoteFile) && this.state.remoteFileUrl === trimmedUrl) {
      this.setState({cropMode: CropMode.Cropping, error: null});
      return;
    }

    const requestId = (this.remoteFetchRequestId += 1);
    this.setState({
      remoteFetchStatus: 'pending',
      remoteFetchError: null,
      remoteFetchRetryable: false,
      error: null
    });

    this.props.fetchRemoteImage(trimmedUrl).then(
      (blob) => {
        // Stale if the URL/tab changed, the modal was reopened or closed,
        // or the component unmounted while this was in flight.
        if (this.isUnmounted || !this.props.isOpen || requestId !== this.remoteFetchRequestId) {
          return;
        }
        this.setState({
          remoteFile: new File([blob], deriveNameFromUrl(trimmedUrl), {type: blob.type}),
          remoteFileUrl: trimmedUrl,
          remoteFetchStatus: 'idle',
          cropMode: CropMode.Cropping
        });
      },
      (err) => {
        if (this.isUnmounted || !this.props.isOpen || requestId !== this.remoteFetchRequestId) {
          return;
        }
        this.setState({
          // Drop back to Preview so the retry UI (rendered there) is
          // reachable.
          cropMode: CropMode.Preview,
          remoteFetchStatus: 'failed',
          remoteFetchError: (err && err.message) || 'Couldn\'t load this image for cropping.',
          remoteFetchRetryable: !isNoU(err) && (err.status === 0 || err.status >= 500)
        });
      }
    );
  };

  // Link tab only -- Device jumps straight from file-select into Cropping
  // (handleDeviceFileSelected). Moves off the raw URL preview: a GIF (never
  // cropped) goes straight to Info, its terminal step; anything else fetches
  // the URL and enters the mandatory crop step -- see the CropMode comment
  // above.
  handleContinueFromPreview = () => {
    const isGif = this.isGifSource();
    const trimmedUrl = this.getTrimmedMediaUrl();
    this.setState({
      imageInfo: {
        name: deriveNameFromUrl(trimmedUrl),
        filename: deriveNameFromUrl(trimmedUrl),
        altAttribute: '',
        source: '',
        link: '',
        sponsoredType: this.props.sponsoredType
      },
      imageInfoErrors: {},
      error: null
    }, () => {
      if (isGif) {
        this.setState({cropMode: CropMode.Info});
      } else {
        this.handleEnterCrop();
      }
    });
  };

  // ResourceForm's onValueChanged contract: (resourcePath, fieldPath, value).
  handleInfoFieldChange = (resourcePath, fieldPath, value) => {
    const field = fieldPath[fieldPath.length - 1];
    this.setState((prevState) => {
      const imageInfoErrors = {...prevState.imageInfoErrors};
      delete imageInfoErrors[field];
      return {
        imageInfo: {...prevState.imageInfo, [field]: value},
        imageInfoErrors
      };
    });
  };

  // Validates Info on submit (matching the legacy form's own validate-on-save
  // timing). Info is now the terminal step -- any crop already happened
  // (or, for a GIF, there was never one to do) -- so once it validates,
  // this confirms the upload directly.
  handleInfoContinue = () => {
    const errors = validateImageInfo(this.state.imageInfo);
    if (Object.keys(errors).length > 0) {
      this.setState({imageInfoErrors: errors});
      return;
    }

    this.handleConfirmClick();
  };

  // Backs out of Info without confirming anything. For a GIF, Info follows
  // Preview/file-select directly, so Back goes there. Otherwise Info follows
  // a completed, reviewed crop, so Back returns to reviewing it rather than
  // discarding the crop.
  handleCancelInfo = () => {
    this.remoteFetchRequestId += 1;
    if (this.isGifSource()) {
      if (this.state.activeTab === DeviceTab) {
        this.handleReplaceFile();
        return;
      }
      this.setState({cropMode: CropMode.Preview, error: null, remoteFetchStatus: 'idle'});
      return;
    }
    this.setState({cropMode: CropMode.ReviewCrop, error: null});
  };

  // Backs out of the cropper without applying anything. Cropping now
  // follows Preview/file-select directly (see the CropMode comment above),
  // so Back goes there: the Device tab has no Preview state of its own, so
  // it deselects the file instead (mirroring ImageCropStep's own "replace"
  // action for that tab); the Link tab just returns to its URL preview,
  // keeping the fetched remoteFile cached for a later re-entry.
  handleCancelCrop = () => {
    if (this.state.activeTab === DeviceTab) {
      this.handleReplaceFile();
      return;
    }
    this.setState({cropMode: CropMode.Preview, error: null});
  };

  // Moves from a reviewed crop into Info, the terminal step.
  handleContinueFromReviewCrop = () => {
    this.setState({cropMode: CropMode.Info, error: null});
  };

  // Applies the crop and shows the result, but does not save yet.
  handleApplyCrop = () => {
    if (isNoU(this.cropStepRef.current)) {
      this.setState({error: 'The crop editor is not ready yet.'});
      return;
    }

    this.setState({isSubmitting: true});
    this.cropStepRef.current.getCroppedFile()
      .then((result) => {
        this.setState({
          isSubmitting: false,
          cropMode: CropMode.ReviewCrop,
          croppedResult: {...result, previewUrl: URL.createObjectURL(result.file)}
        });
      })
      .catch((err) => {
        this.setState({
          isSubmitting: false,
          error: (err && err.message) || 'Failed to process the image.'
        });
      });
  };

  // Discards the crop result and goes back to the cropper for another try.
  handleRecrop = () => {
    if (this.state.croppedResult) {
      URL.revokeObjectURL(this.state.croppedResult.previewUrl);
    }
    this.setState({croppedResult: null, cropMode: CropMode.Cropping, error: null});
  };

  // "Confirm" -- a completed crop (either tab) is sent as preCropped: true.
  // Otherwise (GIFs only, since raster sources always crop before reaching
  // here): the Link tab sends the raw mediaUrl, the Device tab sends the
  // raw file; both with preCropped: false, so the server center-crops to
  // the field's target aspect ratio itself (a no-op for a GIF, which the
  // server stores unresized regardless). name/altAttribute/source always
  // come from Info, which validates (handleInfoContinue) immediately before
  // this is reached -- Info is the terminal step for every source.
  handleConfirmClick = () => {
    const {activeTab, mediaUrl, selectedFile, croppedResult, imageInfo} = this.state;
    const {name, filename, altAttribute, source, link} = imageInfo;

    // croppedResult isn't tab-tagged, so a completed crop left behind on the
    // other (now inactive) tab -- see handleTabChange -- must not be
    // confirmed here just because it's still sitting in state.
    if (!isNoU(croppedResult) && this.hasSource()) {
      this.props.onConfirm({
        file: croppedResult.file,
        name,
        filename,
        altAttribute,
        source,
        link,
        contentType: croppedResult.contentType,
        preCropped: true
      });
      return;
    }

    if (activeTab === LinkTab) {
      const trimmedUrl = this.getTrimmedMediaUrl();
      if (isBlank(trimmedUrl)) {
        this.setState({error: 'Enter an image URL.'});
        return;
      }

      // No client-side content type: a plain (non-cropped) URL upload is
      // never fetched by this component at all -- it's sent straight
      // through as mediaUrl, and the server determines content type when
      // it fetches the bytes itself (the same fetcher §7's Crop path uses).
      this.props.onConfirm({
        mediaUrl: trimmedUrl,
        name,
        filename,
        altAttribute,
        source,
        link,
        contentType: undefined,
        preCropped: false
      });
      return;
    }

    if (isNoU(selectedFile)) {
      this.setState({error: 'Choose an image file.'});
      return;
    }

    this.props.onConfirm({
      file: selectedFile,
      name,
      filename,
      altAttribute,
      source,
      link,
      contentType: selectedFile.type,
      preCropped: false
    });
  };

  isPrimaryDisabled = () => {
    const {activeTab, mediaUrl, cropMode, isSubmitting, error, croppedResult, remoteFetchStatus} = this.state;
    if (isSubmitting) {
      return true;
    }
    if (activeTab === LinkTab && remoteFetchStatus === 'pending') {
      return true;
    }
    // See handleConfirmClick's matching guard -- a completed crop left on
    // the other (inactive) tab must not enable Confirm here.
    if (!isNoU(croppedResult) && this.hasSource()) {
      return false;
    }
    if (cropMode === CropMode.Info) {
      // Validated on click (handleInfoContinue), not pre-disabled here --
      // matches the legacy form's own validate-on-save timing.
      return false;
    }
    if (activeTab === LinkTab && isBlank(mediaUrl)) {
      return true;
    }
    if (!this.hasSource()) {
      return true;
    }
    if (cropMode === CropMode.Cropping) {
      // Applying the crop -- only disabled if there's an outstanding error.
      return !isNoU(error);
    }
    return false;
  };

  render() {
    const {
      activeTab, selectedFile, devicePreviewUrl, mediaUrl, cropMode, croppedResult, error, isSubmitting,
      remoteFile, remoteFetchStatus, remoteFetchError, remoteFetchRetryable, imageInfo, imageInfoErrors
    } = this.state;
    const trimmedMediaUrl = this.getTrimmedMediaUrl();
    const hasSource = this.hasSource();
    const isGif = this.isGifSource();
    const isInfoStep = hasSource && cropMode === CropMode.Info;
    const isCropping = hasSource && cropMode === CropMode.Cropping;
    const isReviewingCrop = hasSource && cropMode === CropMode.ReviewCrop;
    const isFetchingRemote = activeTab === LinkTab && remoteFetchStatus === 'pending';
    const remoteFetchFailed = activeTab === LinkTab && remoteFetchStatus === 'failed';

    const actions = [
      {
        label: 'Cancel',
        isDisabled: isSubmitting,
        displayType: 'menu-item',
        action: this.handleCancel
      }
    ];

    if (isCropping) {
      actions.push({
        label: 'Back',
        isDisabled: isSubmitting,
        displayType: 'menu-item',
        action: this.handleCancelCrop
      });
    } else if (isReviewingCrop) {
      actions.push({
        label: 'Recrop',
        isDisabled: isSubmitting,
        displayType: 'menu-item',
        action: this.handleRecrop
      });
    } else if (isInfoStep) {
      actions.push({
        label: 'Back',
        isDisabled: isSubmitting,
        displayType: 'menu-item',
        action: this.handleCancelInfo
      });
    }

    // A plain, not-yet-picked preview can only be the Link tab waiting on a
    // URL (the Device tab jumps straight into Cropping/Info when a file is
    // picked). Its primary action moves on to Cropping (or, for a GIF,
    // straight to Info) instead of confirming the raw image directly.
    const needsToLeavePreview = hasSource && cropMode === CropMode.Preview;

    let primaryLabel = 'Confirm';
    let primaryAction = this.handleConfirmClick;
    if (isCropping) {
      primaryLabel = 'Apply Crop';
      primaryAction = this.handleApplyCrop;
    } else if (isReviewingCrop) {
      primaryLabel = 'Continue';
      primaryAction = this.handleContinueFromReviewCrop;
    } else if (isInfoStep) {
      primaryLabel = 'Confirm';
      primaryAction = this.handleInfoContinue;
    } else if (needsToLeavePreview) {
      primaryLabel = 'Continue';
      primaryAction = this.handleContinueFromPreview;
    }

    actions.push({
      label: primaryLabel,
      isDisabled: this.isPrimaryDisabled() || isFetchingRemote,
      loading: isSubmitting || isFetchingRemote,
      displayType: 'button',
      primaryCheck: true,
      action: primaryAction
    });

    return (
      <BasicModal
        open={this.props.isOpen}
        header="Upload Image"
        keepModalOnTop={true}
        specialType="primary-modal"
        onClose={this.handleCancel}
        // Semantic's default dimmer-click-to-close fires off the *click*
        // event's target, not the mousedown's -- so selecting text in a
        // field (e.g. dragging to select the Filename value) and releasing
        // the mouse outside the modal reads as an outside click and closes
        // it, discarding whatever was picked/entered. Turned off so only
        // Escape or an explicit action (Cancel, the X, Back) closes this.
        closeOnDimmerClick={false}
        actions={actions}>
        <Menu tabular>
          <Menu.Item name={DeviceTab} active={activeTab === DeviceTab} onClick={() => this.handleTabChange(DeviceTab)}>
            My Device
          </Menu.Item>
          <Menu.Item name={LinkTab} active={activeTab === LinkTab} onClick={() => this.handleTabChange(LinkTab)}>
            Link (URL)
          </Menu.Item>
        </Menu>

        {error && <Message negative>{error}</Message>}

        {activeTab === DeviceTab && isNoU(selectedFile) && (
          <ImageUploadDeviceTab
            onFileSelected={this.handleDeviceFileSelected}
            onError={this.handleDeviceError} />
        )}

        {activeTab === LinkTab && (
          <ImageUploadLinkTab value={mediaUrl} onChange={this.handleMediaUrlChange} />
        )}

        {hasSource && (
          isCropping ? (
            // The crop step only ever sees a same-origin File: the Device
            // tab's selectedFile, or the Link tab's remoteFile fetched
            // through GET /image/fetch by handleEnterCrop above.
            <ImageCropStep
              ref={this.cropStepRef}
              file={activeTab === DeviceTab ? selectedFile : remoteFile}
              imageType={this.props.imageType}
              onError={this.handleDeviceError}
              onReplace={activeTab === DeviceTab ? this.handleReplaceFile : this.handleCancelCrop}
              replaceLabel={activeTab === DeviceTab ? 'Choose a different file' : 'Cancel crop'} />
          ) : isReviewingCrop ? (
            <div className="db-image-crop-review">
              <img src={croppedResult.previewUrl} alt={croppedResult.name} style={{maxWidth: '100%'}} />
            </div>
          ) : isFetchingRemote ? (
            <div className="db-image-crop-step">
              <Loader active inline="centered">Loading image for cropping…</Loader>
            </div>
          ) : isInfoStep ? (
            <ImageUploadInfoStep
              formData={imageInfo}
              errors={imageInfoErrors}
              // Info now follows the crop step, so show what was actually
              // cropped -- not the original -- except for a GIF, which was
              // never cropped and has no croppedResult to show instead.
              // croppedResult isn't tab-tagged (see handleConfirmClick's
              // matching guard), so a completed crop left on the other,
              // now-inactive tab must not be shown here either.
              previewUrl={(!isNoU(croppedResult) && hasSource)
                ? croppedResult.previewUrl
                : (activeTab === DeviceTab ? devicePreviewUrl : trimmedMediaUrl)}
              onValueChanged={this.handleInfoFieldChange} />
          ) : (
            // Link tab only -- Device jumps straight from file-select into
            // Cropping or Info (handleDeviceFileSelected), so this raw
            // preview (Preview mode, hasSource true) is never reached for it.
            <div className="db-image-crop-preview">
              <img src={trimmedMediaUrl} alt="Linked image preview" style={{maxWidth: '100%'}} />
              {isGif && <p>Animated GIFs are uploaded as-is and are not cropped.</p>}
              {remoteFetchFailed && (
                <Message negative>
                  <p>{remoteFetchError}</p>
                  {remoteFetchRetryable && (
                    <Button type="button" basic onClick={this.handleEnterCrop}>Try again</Button>
                  )}
                </Message>
              )}
            </div>
          )
        )}
      </BasicModal>
    );
  }
}

ImageUploadModal.propTypes = {
  imageType: string.isRequired,
  isOpen: bool.isRequired,
  onCancel: func.isRequired,
  onConfirm: func.isRequired,
  fetchRemoteImage: func.isRequired,
  // Drives the Info step's "source required" rule, matching
  // imageFormConfig.js -- undefined is fine (the rule just never fires).
  sponsoredType: string
};



// WEBPACK FOOTER //
// ./public/components/images/ImageUploadModal.jsx